import React, { FC, useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { IAppState } from 'reducers';
import { setDIAIPAllocation as setDIAIPAllocationAction, setIPOption as setIPOptionAction } from 'Quotes/actions';
import { IPOption, IPType, staticIPList, subnetList } from './types/ip';
import { IAEndLocation, IPAddress } from 'Quotes/types/store';
import { Tooltip } from 'shared/components/atoms/Tooltip';
import { ISetDIAIPAllocation } from 'Quotes/types/actions';
import { diaIPAllocationOptions, DualCircuitRouterConfigurations, IPAddressAllocation } from './types/diaIPAllocation';
import { Option, OptionGroup } from 'shared/components/organisms/OptionGroup/OptionGroup';
import hasFeatureEnabled, { featureFlag } from 'FeatureFlags/utils/hasFeatureEnabled';
import { Feature } from 'FeatureFlags/types';
import { RackMountKit } from 'Quotes/QuoteBuilder/components/Configure/DIAConfig/RackMount';
import { EngineerInstallationRequired } from 'Quotes/QuoteBuilder/components/Configure/DIAConfig/EngineerInstallation';
import { MDIASelection } from 'Quotes/QuoteBuilder/components/Configure/DIAConfig/MDIASelection';
import { generateIPLabelWithUsableAddresses } from 'Quotes/QuoteBuilder/components/Configure/DIAConfig/utils/generateIPLabelWithUsableAddresses';
import { selectQuote, selectSelectedPrice } from 'Quotes/selectors';
import { selectIsIndependentRouter, RoutingOption } from './RoutingOption';

const tooltipText = hasFeatureEnabled(Feature.newMDIAUI)
  ? `Mandatory justification is required by RIPE for orders of 16 or more static IP addresses (/28,/27 and /26 subnets).`
  : `If you require a WAN IP (/31 subnet) interface in addition to the public addresses selected,
please select this option when completing the order form.
Mandatory justification is required by RIPE for orders of 16 static IP addresses.`;

interface DIAConfig {
  className?: string;
  ip: IPAddress;
  isManagedDIA: IAEndLocation['is_managed_dia'];
  diaIPAllocation: IAEndLocation['dia_ip_allocation'];
  setIPOption(option: IPType): void;
  setDIAIPAllocation(dia_ip_allocation: ISetDIAIPAllocation['payload']['dia_ip_allocation']): void;
  readonly?: boolean;
}

export const IP_ADDRESS_SELECTION_AVAILABLE = [
  IPAddressAllocation.WAN_31_PUBLIC_ALLOCATION,
  IPAddressAllocation.PUBLIC_ALLOCATION_ONLY,
];

export const getAdditionalIPAddressesHelperText = (
  is_managed_dia: boolean,
  dia_ip_allocation: IPAddressAllocation | null
) => {
  if (is_managed_dia) return 'Note: 1 IP will be required on the MDIA router LAN interface.';
  else if (dia_ip_allocation === IPAddressAllocation.PUBLIC_ALLOCATION_ONLY)
    return 'Note: 1 IP will be used on upstream Neos router.';

  return '';
};

export type DIAChangeSnapshot = {
  is_managed_dia: IAEndLocation['is_managed_dia'];
  dia_ip_allocation: IAEndLocation['dia_ip_allocation'];
  ip_selected: IPAddress['selectedId'];
  is_engineer_installation_required: IAEndLocation['is_engineer_installation_required'];
  is_rack_mount_required: IAEndLocation['is_rack_mount_required'];
  routerChoice: IAEndLocation['routerChoice'];
  secondRouterOptions: IAEndLocation['secondRouterOptions'];
  secondIPChoice: IAEndLocation['secondIPChoice'];
};

export const getDIAChangeSnapshot = (aEnd: IAEndLocation): DIAChangeSnapshot => ({
  is_managed_dia: aEnd.is_managed_dia,
  dia_ip_allocation: aEnd.dia_ip_allocation,
  ip_selected: aEnd.ip?.selectedId,
  is_engineer_installation_required: aEnd.is_engineer_installation_required,
  is_rack_mount_required: aEnd.is_rack_mount_required,
  routerChoice: aEnd.routerChoice,
  secondRouterOptions: aEnd.secondRouterOptions,
  secondIPChoice: aEnd.secondIPChoice,
});

type DIAConfigProps = { 
  onAnyChange?: (next: DIAChangeSnapshot, prev: DIAChangeSnapshot) => void 
  readonly?: boolean
};

export const DIAConfig: FC<DIAConfigProps> = ({ onAnyChange, readonly }) => {
  const dispatch = useDispatch();
  const setIPOption = (option: IPType) => dispatch(setIPOptionAction(option));
  const setDIAIPAllocation = (diaIPAllocation: IPAddressAllocation) =>
    dispatch(setDIAIPAllocationAction(diaIPAllocation));

  const ip = useSelector((state: IAppState) => state.quoteBuilder.quote.location.aEnd.ip);
  const isManagedDIA = useSelector((state: IAppState) => state.quoteBuilder.quote.location.aEnd.is_managed_dia);
  const diaIPAllocation = useSelector((state: IAppState) => state.quoteBuilder.quote.location.aEnd.dia_ip_allocation);

  // Trigger the parent save handler whenever DIA-related selections change
  const prevSnapshot = useRef<DIAChangeSnapshot | undefined>();
  const skipNextUpdate = useRef(false);
  const currentSnapshot = useSelector(
    (state: IAppState) => getDIAChangeSnapshot(state.quoteBuilder.quote.location.aEnd),
    shallowEqual
  );

  // Skip specific changes as it causes double updates
  useEffect(() => {
    if (!prevSnapshot.current) {
      prevSnapshot.current = { ...currentSnapshot };
      return;
    }

    // Skip for dia_ip_allocation changes to specific values
    if (currentSnapshot.dia_ip_allocation) {
      const isTargetValue = [
        IPAddressAllocation.WAN_31_PUBLIC_ALLOCATION,
        IPAddressAllocation.PUBLIC_ALLOCATION_ONLY,
      ].includes(currentSnapshot.dia_ip_allocation);

      if (isTargetValue && prevSnapshot.current.dia_ip_allocation !== currentSnapshot.dia_ip_allocation) {
        skipNextUpdate.current = true;
      }
    }

    // Skip for is_managed_dia changes from true to false (yes to no)
    if (prevSnapshot.current.is_managed_dia === true && currentSnapshot.is_managed_dia === false) {
      if(prevSnapshot.current.dia_ip_allocation !== IPAddressAllocation.WAN_ONLY_31 && currentSnapshot.dia_ip_allocation === IPAddressAllocation.WAN_ONLY_31){
        // Do not skip if dia_ip_allocation is changing from non-WAN_ONLY_31 to WAN_ONLY_31
        skipNextUpdate.current = false;
      } else {
        skipNextUpdate.current = true;
      }
    }

    // Skip when MDIA is enabled but no IP is selected yet
    // if (prevSnapshot.current.is_managed_dia === false && currentSnapshot.is_managed_dia === true && !currentSnapshot.ip_selected) {
    //   skipNextUpdate.current = true;
    // }

    // skip if no IP is selected yet
    if (!currentSnapshot.ip_selected) {
      skipNextUpdate.current = true;
    }
  }, [currentSnapshot]);

  useEffect(() => {
    // Skip initial mount
    if (prevSnapshot.current) {
      // Skip the update if the flag is set
      if (skipNextUpdate.current) {
        skipNextUpdate.current = false;
      } else {
        // Pass both snapshots so parent decides
        onAnyChange?.(currentSnapshot, prevSnapshot.current);
      }
    }
    prevSnapshot.current = { ...currentSnapshot };
  }, [currentSnapshot, onAnyChange]);

  return (
    <DIAConfigInternal
      ip={ip}
      setIPOption={setIPOption}
      isManagedDIA={isManagedDIA}
      diaIPAllocation={diaIPAllocation}
      setDIAIPAllocation={setDIAIPAllocation}
      readonly={readonly}
    />
  );
};

function Header(props: { typeText?: string; additionalHelperText: false | string; marginTop: string }) {
  return (
    <div >
      <div className={`mt-${props.marginTop} mb-2 ip-addresses-label addon_options__title`}>
        {props.typeText} IP Addresses
        <Tooltip text={tooltipText} />
      </div>
      <p className="text-muted mb-4">
        Please select your {props.typeText} public address space. {props.additionalHelperText}
      </p>
    </div>
  );
}

function IPSelection(props: {
  options: Option<IPType>[];
  onClick: (value: IPType) => void;
  value: IPType | null | undefined;
  readOnly?: boolean;
}) {
  return (
    <div className="d-flex flex-wrap">
      <OptionGroup<IPType>
        options={props.options}
        onClick={props.onClick}
        value={props.value}
        itemsPerRow={3}
        readOnly={props.readOnly}
      />
    </div>
  );
}

export const IPAddressSelection = ({
  additionalHelperText,
  ipOption,
  options,
  setIPOption,
  typeText,
  isReadOnly,
  showHeader = true,
  marginTop = '4',
}: {
  additionalHelperText: false | string;
  options: Option<IPType>[];
  setIPOption: (value: IPType) => void;
  ipOption: IPType | null | undefined;
  typeText?: string;
  isReadOnly?: boolean;
  showHeader?: boolean;
  marginTop?: string;
}) => (
  <>
    <div className='ip-addresses-section' data-testid="ip-addresses-section">
      {showHeader && <Header typeText={typeText} additionalHelperText={additionalHelperText} marginTop={marginTop} />}
      <IPSelection options={options} onClick={setIPOption} value={ipOption} readOnly={isReadOnly} />
    </div>
  </>
);

export const IPAddressAllocationSelection = ({
  diaIPAllocation,
  setDIAIPAllocation,
  readonly,
  headerAddon,
  routerChoice,
  isManagedDIA,
  isRO2Enabled
}: {
  diaIPAllocation: IPAddressAllocation | null;
  setDIAIPAllocation: (value: IPAddressAllocation) => void;
  readonly?: boolean;
  headerAddon?: string;
  routerChoice?: DualCircuitRouterConfigurations | null;
  isManagedDIA?: boolean;
  isRO2Enabled?: boolean;
}) => {
    
  let ipAllocationOptions = diaIPAllocationOptions
  .filter(item => item.id !== IPAddressAllocation.IP_TRANSIT); // Hide IP Transit until correct cerillion package is available

  // For RO2, If DIA and BGP, only show WAN /31 + public
  if (isRO2Enabled === true && isManagedDIA === false 
    && routerChoice === DualCircuitRouterConfigurations.DUAL_FAILOVER_ACTIVE_ACTIVE) {
    ipAllocationOptions = ipAllocationOptions.filter(item => item.id === IPAddressAllocation.WAN_31_PUBLIC_ALLOCATION);
  }

  const options = ipAllocationOptions.map((item) => ({
    customId: item.id,
    label: item.label,
    value: item.id,
    tooltip: item.helperText,
  }));

  return (
    <>
      <p className="mt-4 mb-2 addon_options__title">IP address allocation {headerAddon}</p>
      <OptionGroup<IPAddressAllocation>
        options={options}
        onClick={setDIAIPAllocation}
        value={diaIPAllocation}
        itemsPerRow={3}
        readOnly={readonly}
      />
    </>
  );
};

const getIpAddressOptions = (includeWANOption: boolean, newMDIAFeatureEnabled: boolean) => {
  const wanFilter = (ipOption: IPOption) => (includeWANOption && ipOption.subnet === 31) || ipOption.subnet !== 31;
  if (newMDIAFeatureEnabled) {
    return subnetList.filter(wanFilter);
  }

  return staticIPList.filter(wanFilter);
};

const getLabelFormatter = (
  isManagedDIA: boolean,
  diaIPAllocation: IPAddressAllocation | null,
  newMDIAFeatureEnabled: boolean,
  interimFix: boolean
) => {
  const staticIpLabel = (ipOption: IPOption): string => ipOption.label();
  const usableAddressesIpLabel = (ipOption: IPOption): string =>
    generateIPLabelWithUsableAddresses(isManagedDIA, ipOption.subnet, diaIPAllocation!);

  return newMDIAFeatureEnabled && !interimFix ? usableAddressesIpLabel : staticIpLabel;
};

export const shouldIncludeWANOption = (
  isManagedDIA: boolean,
  diaIPAllocation: IPAddressAllocation | null,
  newMDIAFeatureEnabled: boolean,
  interimFix: boolean
): boolean => {
  if (newMDIAFeatureEnabled && !interimFix) return diaIPAllocation === IPAddressAllocation.WAN_31_PUBLIC_ALLOCATION;
  else return !isManagedDIA;
};

export const getIpAddressesOptions = (
  isManagedDIA: boolean,
  diaIPAllocation: IPAddressAllocation | null,
  newMDIAFeatureEnabled: boolean,
  interimFix: boolean
) => {
  const labelFormatter = getLabelFormatter(isManagedDIA, diaIPAllocation, newMDIAFeatureEnabled, true);

  const includeWAN = shouldIncludeWANOption(isManagedDIA, diaIPAllocation, newMDIAFeatureEnabled, interimFix);

  return getIpAddressOptions(includeWAN, newMDIAFeatureEnabled).map((ipOption) => ({
    customId: ipOption.id,
    label: labelFormatter(ipOption),
    value: ipOption.id,
  }));
};
  
export const DIAConfigInternal: FC<React.PropsWithChildren<DIAConfig>> = ({
  className = '',
  ip,
  setIPOption,
  isManagedDIA,
  diaIPAllocation,
  setDIAIPAllocation,
  readonly,
}) => {
  const newMDIAFeatureEnabled = featureFlag.isEnabled(Feature.newMDIAUI);
  const interimFix = (newMDIAFeatureEnabled && featureFlag.getAttribute<boolean>(Feature.newMDIAUI, 'interimFix')) || false;

  const ipAddressesOptions = getIpAddressesOptions(isManagedDIA, diaIPAllocation, newMDIAFeatureEnabled, interimFix);

  const additionalHelperText =
    newMDIAFeatureEnabled && getAdditionalIPAddressesHelperText(isManagedDIA, diaIPAllocation);

  const supportsIPAddressSelection =
    diaIPAllocation !== null && IP_ADDRESS_SELECTION_AVAILABLE.includes(diaIPAllocation);

  const quote = useSelector(selectQuote);
  const selectedPrice = useSelector(selectSelectedPrice);
  const isRO2Enabled = selectedPrice.secondary_circuits?.enabled ?? false;

  const extraIPAddressAllocationHeaderForIndependentRouter = selectIsIndependentRouter(quote) === true ? (" (Primary and Secondary)") : ""

  if (newMDIAFeatureEnabled) {
    return (
      <div className={`${className} mb-4 w-100`} data-testid="dia-addons-section">

        <MDIASelection readonly={readonly} />

        {isRO2Enabled && <RoutingOption readonly={readonly} />}

        {!isManagedDIA && !interimFix && (
          <IPAddressAllocationSelection 
            setDIAIPAllocation={setDIAIPAllocation} 
            diaIPAllocation={diaIPAllocation}
            readonly={readonly}
            headerAddon={extraIPAddressAllocationHeaderForIndependentRouter}
            routerChoice={quote.location.aEnd.routerChoice}
            isManagedDIA={isManagedDIA}
            isRO2Enabled={isRO2Enabled}
          />
        )}

        {(isManagedDIA || interimFix || supportsIPAddressSelection) && (
          <IPAddressSelection
            additionalHelperText={additionalHelperText}
            options={ipAddressesOptions}
            setIPOption={setIPOption}
            ipOption={ip.selectedId}
            isReadOnly={readonly}
          />
        )}

        {isManagedDIA && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ width: '48%' }}>
              <EngineerInstallationRequired isReadOnly={readonly} />
            </div>
            <div style={{ width: '48%' }}>
              <RackMountKit readonly={readonly} />
            </div>
          </div>
        )}
      </div>
    );
  } else
    return (
      <div className={`${className} mb-4`}>
        <MDIASelection readonly={readonly} />

        <IPAddressSelection
          additionalHelperText={additionalHelperText}
          options={ipAddressesOptions}
          setIPOption={setIPOption}
          ipOption={ip.selectedId}
          isReadOnly={readonly}
        />
      </div>
    );
};

const styledDIAConfig = styled(DIAConfig)`
  .radio-btn {
    width: 33.333%;
    padding-right: 0.625em;
    &:nth-child(2) {
      padding-left: 0.625em;
    }
  }
`;

export default styledDIAConfig;
